import { useState } from 'react';
import {
  useLegalEntities,
  useCreateLegalEntity,
  useUpdateLegalEntity,
  useDeleteLegalEntity,
} from '../../../hooks/useLegalEntities';
import { useCountries } from '../../../hooks/useCountries';
import { useProvinces } from '../../../hooks/useProvinces';
import { useDepartments } from '../../../hooks/useDepartments';
import { useDistricts } from '../../../hooks/useDistricts';
import { ITEMS_PER_PAGE, INITIAL_LEGAL_ENTITY_STATE } from '../constants/legalEntitiesConstants';
import { filterLegalEntities } from '../utils/legalEntitiesUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

/**
 * Hook personalizado para gestionar el estado y lógica de la tabla de personas jurídicas
 */
export const useLegalEntitiesTable = () => {
  // Estado local
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedLegalEntity, setSelectedLegalEntity] = useState(INITIAL_LEGAL_ENTITY_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  // Queries
  const { data: legalEntities = [], isLoading } = useLegalEntities();
  const { data: countries = [] } = useCountries();
  const { data: provinces = [] } = useProvinces();
  const { data: departments = [] } = useDepartments();
  const { data: districts = [] } = useDistricts();

  // Mutations
  const createMutation = useCreateLegalEntity();
  const updateMutation = useUpdateLegalEntity();
  const deleteMutation = useDeleteLegalEntity();

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: legalEntities,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterLegalEntities,
  });

  const handleOpenDialog = () => {
    setSelectedLegalEntity(INITIAL_LEGAL_ENTITY_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedLegalEntity((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setSelectedLegalEntity((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setSelectedLegalEntity((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSave = async () => {
    if (editMode) {
      updateMutation.mutate(selectedLegalEntity);
    } else {
      createMutation.mutate(selectedLegalEntity);
    }
    handleCloseDialog();
  };

  const handleEdit = (legalEntity) => {
    setSelectedLegalEntity(legalEntity);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = (legalEntity) => {
    setEntityToDelete(legalEntity);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (entityToDelete) {
      deleteMutation.mutate(entityToDelete.uuid);
      setDeleteConfirmOpen(false);
      setEntityToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEntityToDelete(null);
  };

  return {
    // Datos
    data: paginatedData,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedLegalEntity,
    deleteConfirmOpen,
    entityToDelete,
    countries,
    provinces,
    departments,
    districts,

    // Manejadores
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
